import React, {
  useState, useContext, useMemo, useEffect,
} from 'react';
import { useHistory } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { useForm, FormProvider } from 'react-hook-form';

import CustomBtn from '../../../../../../generic/CustomBtn';
import CarbonFormHeader from '../CarbonFormHeader';
import CarbonFormTabsButtons from '../CarbonFormTabsButtons';
import GeneralInformation from '../GeneralInformation';
import ProjectActivity from '../ProjectActivity';
import LandInformation from '../LandInformation';
import LegalAndFinancial from '../LegalAndFinancial';
import Risks from '../Risks';
import ContactInfo from '../ContactInfo';
import Loader from '../../../../../../components/Loader';
import RouteLeavingGuard from '../../../../../../components/RouteLeavingGuard';
import LeavePopup from '../LeavePopup';
import './CarbonForm.scss';

import getConfig from '../../../../../../config';
import { appStore } from '../../../../../../state/app';
import {
  getProjectReference, makeFileSenderByType, milliToNano,
} from '../../../../../../utils/helpers';
import { buildGatewayUrl, initIPFS } from '../../../../../../state/ipfs';
import { getContract, contractMethods, isWalletCancelError } from '../../../../../../utils/near-utils';
import { GAS } from '../../../../../../state/near';
import {
  handleResponseStatus, sendRequest, chooseReUpdateData,
} from '../../../../../../utils/api-request';
import { toastifyMessage } from '../../../../../../components/Toastify/Toastify';
import saveCarbonFileList from '../../../../../../services/saveCarbonFileList';
import deleteFileFromCarbonList from '../../../../../../services/deleteFileFromCarbonList';
import Criteria from '../Criteria';

const { deposit, backendApi } = getConfig();

const getTabs = () => ([
  {
    component: Criteria,
    data: {
      tabId: 0,
      tabKey: 'creteriaEligibility',
      label: 'carbon.form.criteria',
      private: false,
    },
  },
  {
    component: GeneralInformation,
    data: {
      tabId: 1,
      tabKey: 'generalInfo',
      label: 'carbon.form.generalInfo',
      private: false,
    },
  },
  {
    component: ProjectActivity,
    data: {
      tabId: 2,
      tabKey: 'projectActivity',
      label: 'carbon.form.projectActivity',
      private: false,
    },
  },
  {
    component: LandInformation,
    data: {
      tabId: 3,
      tabKey: 'landInfo',
      label: 'carbon.form.landInfo',
      private: false,
    },
  },
  {
    component: LegalAndFinancial,
    data: {
      tabId: 4,
      tabKey: 'legalAndFinancial',
      label: 'carbon.form.legalAndFinancial',
      private: false,
    },
  },
  {
    component: Risks,
    data: {
      tabId: 5,
      tabKey: 'risks',
      label: 'carbon.form.risks',
      private: false,
    },
  },
  {
    component: ContactInfo,
    data: {
      tabId: 6,
      tabKey: 'contactInfo',
      label: 'carbon.form.contactInfo',
      private: true,
    },
  },
]);

const CarbonForm = ({
  projectData,
  carbonData,
  setIsError,
  initialTabKey,
}) => {
  const translate = useIntl();
  const tabs = useMemo(() => getTabs(translate), []);
  const history = useHistory();
  const { state: { account, app }, update } = useContext(appStore);
  const { filesOther, filesGen, toDeleteList, onSubmitedCarbonForm } = app;
  const [defaultFormData, setDefaultFormData] = useState(structuredClone(carbonData));
  const methods = useForm({ defaultValues: defaultFormData });
  const [isLoading, setIsLoading] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [blockedTab, setBlockedTab] = useState();
  const [activeTab, setActiveTab] = useState(tabs[0]);
  const isDirtyFiles = !!filesOther.length || !!filesGen.length || !!toDeleteList.length;
  const {
    data: {
      tabId: activeTabId,
      tabKey: activeTabKey,
      // eslint-disable-next-line no-unused-vars
      private: isActiveTabPrivate,

    },
  } = activeTab;
  const { handleSubmit, errors, clearErrors, getValues, reset, formState: { isDirty } } = methods;
  const filterFormData = (data) => {
    const allFields = structuredClone(data);
    const privateData = {};
    const publicData = {};

    tabs.forEach((tab) => {
      const { data: { tabKey, private: isPrivate } } = tab;

      if (isPrivate) {
        privateData[tabKey] = allFields[tabKey];
        return;
      }
      publicData[tabKey] = allFields[tabKey];
    });

    return { privateData, publicData };
  };

  const updateProjectMetadata = async (data) => {
    const ipfs = await initIPFS();

    // Send new project data to IPFS
    const newProjectCid = await ipfs.add(JSON.stringify(data));
    if (!newProjectCid?.path) throw new Error('New project cid is missing');
    // Update project in smart-contract
    const { project_id: projectId, name, iconCidDir, type } = projectData;
    const contract = getContract(account, contractMethods, 0);
    const prevReference = await getProjectReference(contract, projectId);

    if (!prevReference) throw new Error('Previous reference is missing');

    await contract.update_project_metadata({
      project_id: projectId,
      project_type: type,
      title: name,
      media: buildGatewayUrl(iconCidDir),
      reference: `/ipfs/${newProjectCid.path}`,
      prev_reference: prevReference,
      additional_duration: null,
    }, GAS, deposit);
  };

  const processLeakageData = async (updProjectData) => {
    const historyAddedAt = milliToNano(Date.now()).toString();
    const leakageItem = {
      ...updProjectData.leakage,
      historyAddedAt,
    };
    updProjectData.leakageHistory = updProjectData.leakageHistory || [];
    updProjectData.leakageHistory.push(leakageItem);
    delete updProjectData.leakage;
  };

  const savePublicData = async (data, completed = false) => {
    if (!projectData) {
      console.error('Project data is missing');
      return false;
    }

    try {
      const updProjectData = structuredClone(projectData);
      const ipfs = await initIPFS();

      if (projectData.leakage && !projectData.leakage.carbonApprove && completed) {
        await processLeakageData(updProjectData);
      }

      const carbonCid = await ipfs.add(JSON.stringify(data));
      if (!carbonCid?.path) {
        throw new Error('Failed to retrieve Carbon CID from IPFS');
      }

      const submittedAt = completed ? milliToNano(Date.now()).toString() : '';
      const { project_id: projectId } = updProjectData;

      const reUpdateProjectData = await chooseReUpdateData(projectId, account, { ...updProjectData });
      await updateProjectMetadata({
        ...reUpdateProjectData,
        carbonApplication: {
          cid: `/ipfs/${carbonCid.path}`,
          completed,
          submittedAt,
        },
      });
      return true;
    } catch (e) {
      if (isWalletCancelError(e)) {
        return false;
      }
      console.error('Error in savePublicData:', e);
      setIsError(true);
      return false;
    }
  };

  const savePrivateData = async (data, completed = false) => {
    try {
      const structuredData = {
        fields: data,
        project_id: projectData.project_id,
        completed,
        documentationComment: data?.documentationComment,
      };

      // If public (or private) data doesn't exist, create carbonApplication in smart-contract first.
      if (!projectData.carbonApplication) {
        const reUpdateProjectData = await chooseReUpdateData(projectData.project_id, account, { ...projectData });
        await updateProjectMetadata({
          ...reUpdateProjectData,
          carbonApplication: {
            cid: '',
            completed: false,
            submittedAt: '',
          },
        });
      }

      // Save private data snapshot to DB after successful contract call (if needed).
      const { statusCode } = await sendRequest(`${backendApi}/api/dashboard/save-carbon-form`, 'POST', JSON.stringify(structuredData), true);
      return handleResponseStatus(
        statusCode,
        () => true,
        () => {
          throw new Error();
        },
      );
    } catch (e) {
      if (isWalletCancelError(e)) {
        return false;
      }
      console.log(e);
      setIsError(true);
      return false;
    }
  };

  const saveActiveTab = async (currentTab, newInitialTab = currentTab) => {
    const {
      data: {
        tabKey: currentTabKey,
        private: isPrivate,
      },
    } = currentTab;
    const activeTabValues = getValues()[currentTabKey];
    const newData = { ...defaultFormData, [currentTabKey]: activeTabValues };
    const { privateData, publicData } = filterFormData(newData);
    clearErrors();
    setDefaultFormData(structuredClone(newData));
    reset(structuredClone(newData));
    setIsLoading(true);
    // eslint-disable-next-line no-use-before-define
    await sendFilesCarbon();

    let done = false;
    if (isPrivate) {
      done = await savePrivateData(privateData[currentTabKey]);
      if (done) {
        toastifyMessage(translate.formatMessage({ id: 'smart-contract-notify' }));
      }
    } else {
      done = await savePublicData(publicData);
    }

    setActiveTab(done ? newInitialTab : currentTab);
    // eslint-disable-next-line no-use-before-define
    setIsLoading(false);
  };
  const sendFilesCarbon = async () => {
    setIsError(false);
    try {
      if (toDeleteList.length) {
        await Promise.all(toDeleteList.map(async (id) => deleteFileFromCarbonList({ file_id: id })));
      }
      if (filesGen.length) {
        const res = makeFileSenderByType('general', projectData?.project_id, filesGen);
        await saveCarbonFileList(res);
      }
      if (filesOther.length) {
        const res = makeFileSenderByType('other', projectData?.project_id, filesOther);
        await saveCarbonFileList(res);
      }
      update('app.filesOther', []);
      update('app.filesGen', []);
      update('app.toDeleteList', []);
      update('app.reloadFilesFlag', new Date());
    } catch (err) {
      console.log(err, 'problem with sending file');
      setIsError(true);
    }
  };

  const onSubmit = async (formData) => {
    setIsLoading(true);
    try {
      const { privateData, publicData } = filterFormData(formData);
      setDefaultFormData((prev) => structuredClone({ ...prev, ...formData }));
      reset(structuredClone(formData));
      await sendFilesCarbon();

      await savePublicData(publicData, true);
    } catch (e) {
      if (!isWalletCancelError(e)) {
        console.log(e);
        setIsError(true);
      }
    }
    setIsLoading(false);
  };

  const switchTab = (nextTab) => {
    const { data: { tabKey: nextTabKey } } = nextTab;
    if (nextTabKey === activeTabKey) return;

    if (isDirty || isDirtyFiles) {
      setShowPopup(true);
      setBlockedTab(nextTab);
      return;
    }
    setActiveTab(nextTab);
  };

  const leaveTab = (targetTab) => {
    // Reset form values to default state when user switches to the new tab without saving
    reset(structuredClone(defaultFormData));
    setActiveTab(targetTab);
    setShowPopup(false);
  };

  const scrollToTop = () => {
    const scrollElement = document.getElementById('layout-dash-scroll');

    if (scrollElement.scrollTop > 430) {
      scrollElement.scrollTop = 430;
    }
  };

  // Get the tab from which user was redirected to the wallet
  useEffect(() => {
    const initialTab = tabs.find(({ data: { tabKey } }) => tabKey === initialTabKey);
    setActiveTab(initialTab || tabs[0]);
  }, []);

  useEffect(() => {
    scrollToTop();
  }, [activeTab]);

  return (
    <RouteLeavingGuard
      when={isDirty || isDirtyFiles}
      navigate={(path) => history.push(path)}
      saveChanges={() => saveActiveTab(activeTab)}
      shouldBlockNavigation={() => (isDirty || isDirtyFiles)} // The argument (if passed) is a location user wants to leave to
    >
      <div className="carbon-form">
        {isLoading && (
          <div className="carbon-form__loader">
            <Loader />
          </div>
        )}
        {/* Display the popup if there is unsaved data and the user switches to a new tab */}
        {showPopup && (
          <LeavePopup
            handleClose={() => setShowPopup(false)}
            handleLeave={() => leaveTab(blockedTab)}
            handleSubmit={() => {
              saveActiveTab(activeTab, blockedTab);
              setShowPopup(false);
            }}
          />
        )}
        <div className="carbon-form__wrapper">
          <CarbonFormHeader
            isSubmitted={onSubmitedCarbonForm}
            leakage={projectData.leakage}
          />
          <div className="carbon-form__tabs-wrapper">
            <CarbonFormTabsButtons
              tabs={tabs}
              activeTabId={activeTabId}
              switchTab={switchTab}
              errors={errors}
            />
            <FormProvider {...methods}>
              <form>
                <div className="carbon-form__tab-body">
                  {tabs.map((t) => {
                    const { data: tabProps, component: TabComponent } = t;
                    const { tabKey, tabId } = tabProps;
                    return (
                      <TabComponent
                        {...tabProps}
                        defaultValues={defaultFormData[tabKey]}
                        isActiveTab={activeTabId === tabId}
                        disabled={onSubmitedCarbonForm}
                        key={`${tabKey}-body`}
                        projectData={projectData}
                      />
                    );
                  })}
                </div>
                {!onSubmitedCarbonForm && (
                  <div className="carbon-form__tab-handlers">
                    {!!activeTabId && (
                    <CustomBtn
                      label={translate.formatMessage({ id: 'button-save' })}
                      handleClick={() => saveActiveTab(activeTab)}
                      customClass="carbon-form__tab-handler carbon-form__tab-handler_filled"
                      disabled={!isDirty && !isDirtyFiles}
                    />
                    )}
                    {activeTabId === tabs.length - 1 ? (
                      <CustomBtn
                        label={translate.formatMessage({ id: 'button-back' })}
                        handleClick={() => switchTab(tabs[activeTabId - 1])}
                        customClass="carbon-form__tab-handler"
                      />
                    ) : (
                      <CustomBtn
                        label={translate.formatMessage({ id: 'button-next' })}
                        handleClick={() => switchTab(tabs[activeTabId + 1])}
                        customClass="carbon-form__tab-handler"
                      />
                    )}
                  </div>
                )}
              </form>
            </FormProvider>
          </div>
        </div>
        {!onSubmitedCarbonForm && !!activeTabId && (
          <div className="carbon-form__submit-wrapper">
            <CustomBtn
              label={projectData?.leakage ? translate.formatMessage({ id: 'button-resubmit' }) : translate.formatMessage({ id: 'button-submit' })}
              handleClick={handleSubmit(onSubmit)}
              type="submit"
              customClass="carbon-form__submit-button"
            />
          </div>
        )}
      </div>
    </RouteLeavingGuard>
  );
};

export default CarbonForm;
